// src/components/modals/SurveyModal.tsx
// User survey modal for beta testing - survey any page
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext.tsx";
import "./SurveyModal.css";

// All surveyable pages with display info
const SURVEY_PAGES = [
  { path: "/dashboard", label: "Dashboard", icon: "📊" },
  { path: "/play-online", label: "Play Online", icon: "🎮" },
  { path: "/multiplayer", label: "Multiplayer", icon: "🌐" },
  { path: "/piece-catalog", label: "Piece Catalog", icon: "📚" },
  { path: "/variant-creator", label: "Variant Creator", icon: "🎨" },
  { path: "/ai-settings", label: "AI Settings", icon: "🤖" },
  { path: "/leaderboard", label: "Leaderboard", icon: "🏆" },
  { path: "/profile", label: "Profile", icon: "👤" },
  { path: "/documents", label: "Documents", icon: "📄" },
  { path: "/help", label: "Help", icon: "❓" },
];

// Page-specific questions
const PAGE_QUESTIONS: Record<string, { intro: string; focusAreas: string[] }> = {
  "/dashboard": {
    intro: "Thank you for reviewing the Dashboard!",
    focusAreas: ["Overview layout", "Statistics display", "Quick actions", "Navigation", "Information clarity"]
  },
  "/play-online": {
    intro: "Thank you for testing the Play Online page!",
    focusAreas: ["Game controls", "Board display", "Move validation", "AI opponent behavior", "Game settings"]
  },
  "/multiplayer": {
    intro: "Thank you for testing the Multiplayer page!",
    focusAreas: ["Room creation", "Joining games", "Real-time moves", "Chat functionality", "Player matching"]
  },
  "/piece-catalog": {
    intro: "Thank you for testing the Piece Catalog!",
    focusAreas: ["Piece information", "Movement patterns", "Navigation", "Visual clarity", "Piece values"]
  },
  "/variant-creator": {
    intro: "Thank you for testing the Variant Creator!",
    focusAreas: ["Board setup", "Piece placement", "Saving variants", "Loading variants", "Rule customization"]
  },
  "/ai-settings": {
    intro: "Thank you for testing the AI Settings!",
    focusAreas: ["Difficulty settings", "AI behavior options", "Settings persistence", "Slider controls", "Preset configurations"]
  },
  "/profile": {
    intro: "Thank you for testing the Profile page!",
    focusAreas: ["Profile information", "Statistics display", "Game history", "Account settings", "Avatar/customization"]
  },
  "/leaderboard": {
    intro: "Thank you for testing the Leaderboard!",
    focusAreas: ["Rankings display", "Filtering options", "Player search", "Rating information", "Navigation"]
  },
  "/documents": {
    intro: "Thank you for reviewing the Documents page!",
    focusAreas: ["Document organization", "Content clarity", "Navigation", "Search functionality", "Readability"]
  },
  "/help": {
    intro: "Thank you for reviewing the Help page!",
    focusAreas: ["Information helpfulness", "FAQ coverage", "Navigation", "Search functionality", "Content clarity"]
  },
  "default": {
    intro: "Thank you for testing this page!",
    focusAreas: ["User interface", "Functionality", "Navigation", "Performance", "Visual design"]
  }
};

interface SurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageName: string;
  pageUrl?: string;
  bugReportId?: number;
}

export const SurveyModal: React.FC<SurveyModalProps> = ({
  isOpen,
  onClose,
  pageName,
  pageUrl,
  bugReportId
}) => {
  const { user } = useAuth();

  // Selected page for survey
  const [selectedPage, setSelectedPage] = useState<string>("/dashboard");

  // Form state
  const [hadProblems, setHadProblems] = useState<string>("none");
  const [problemsDescription, setProblemsDescription] = useState<string>("");
  const [understandingLevel, setUnderstandingLevel] = useState<string>("clear");
  const [confusionDescription, setConfusionDescription] = useState<string>("");
  const [needsImprovement, setNeedsImprovement] = useState<string>("no");
  const [improvementSuggestions, setImprovementSuggestions] = useState<string>("");
  const [overallRating, setOverallRating] = useState<number>(0);
  const [generalComments, setGeneralComments] = useState<string>("");
  const [recommendations, setRecommendations] = useState<string>("");
  const [wouldRecommend, setWouldRecommend] = useState<string>("yes");
  const [reporterName, setReporterName] = useState<string>(user?.username || "");
  const [reporterEmail, setReporterEmail] = useState<string>(user?.email || "");

  // UI state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(1);

  // Get page-specific content based on selected page
  const pageContent = PAGE_QUESTIONS[selectedPage] || PAGE_QUESTIONS["default"];
  const selectedPageInfo = SURVEY_PAGES.find(p => p.path === selectedPage);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Set selected page to current page if it's in the list, otherwise default to Dashboard
      const currentPageInList = SURVEY_PAGES.find(p => p.path === pageUrl);
      setSelectedPage(currentPageInList ? pageUrl || "/dashboard" : "/dashboard");

      setHadProblems("none");
      setProblemsDescription("");
      setUnderstandingLevel("clear");
      setConfusionDescription("");
      setNeedsImprovement("no");
      setImprovementSuggestions("");
      setOverallRating(0);
      setGeneralComments("");
      setRecommendations("");
      setWouldRecommend("yes");
      setReporterName(user?.username || "");
      setReporterEmail(user?.email || "");
      setSubmitError("");
      setSubmitSuccess(false);
      setCurrentStep(1);
    }
  }, [isOpen, user, pageUrl]);

  // Handle submit
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const response = await fetch(`${apiUrl}/api/surveys`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          bug_report_id: bugReportId || null,
          page_name: selectedPageInfo?.label || pageName,
          page_url: selectedPage,
          had_problems: hadProblems,
          problems_description: problemsDescription,
          understanding_level: understandingLevel,
          confusion_description: confusionDescription,
          needs_improvement: needsImprovement,
          improvement_suggestions: improvementSuggestions,
          overall_rating: overallRating > 0 ? overallRating : null,
          general_comments: generalComments,
          recommendations: recommendations,
          would_recommend: wouldRecommend,
          user_id: user?.userId || null,
          reporter_name: reporterName,
          reporter_email: reporterEmail,
          browser_info: navigator.userAgent,
          screen_resolution: `${window.screen.width}x${window.screen.height}`
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit survey");
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error: any) {
      console.error("Survey submission error:", error);
      setSubmitError(error.message || "Failed to submit survey. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Success screen
  if (submitSuccess) {
    return (
      <div className="survey-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="survey-modal survey-success">
          <div className="survey-success-icon">&#10003;</div>
          <h2>Survey Submitted!</h2>
          <p>Thank you for your valuable feedback.</p>
          <p>Your input helps us improve Expanded Chess.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="survey-modal">
        {/* Header */}
        <div className="survey-header">
          <h2>Quick Survey</h2>
          <button className="survey-close" onClick={onClose} title="Close">
            &times;
          </button>
        </div>

        {/* Page Selection Tabs */}
        <div className="survey-page-tabs">
          <div className="page-tabs-label">Select a page to survey:</div>
          <div className="page-tabs-container">
            {SURVEY_PAGES.map((page) => (
              <button
                key={page.path}
                className={`page-tab ${selectedPage === page.path ? 'active' : ''}`}
                onClick={() => setSelectedPage(page.path)}
                title={page.label}
              >
                <span className="page-tab-icon">{page.icon}</span>
                <span className="page-tab-label">{page.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Progress indicator */}
        <div className="survey-progress">
          <div className={`progress-dot ${currentStep >= 1 ? 'active' : ''}`}>1</div>
          <div className="progress-line"></div>
          <div className={`progress-dot ${currentStep >= 2 ? 'active' : ''}`}>2</div>
          <div className="progress-line"></div>
          <div className={`progress-dot ${currentStep >= 3 ? 'active' : ''}`}>3</div>
        </div>

        {/* Error */}
        {submitError && (
          <div className="survey-error">{submitError}</div>
        )}

        {/* Step 1: Problems & Understanding */}
        {currentStep === 1 && (
          <div className="survey-step">
            <div className="survey-intro">
              <p>{pageContent.intro}</p>
              <p className="focus-note">Focus areas: {pageContent.focusAreas.join(", ")}</p>
            </div>

            {/* Problems */}
            <div className="survey-question">
              <label>Did you encounter any problems or issues?</label>
              <div className="radio-group">
                {[
                  { value: "none", label: "No problems", icon: "✅" },
                  { value: "minor", label: "Minor issues", icon: "🟡" },
                  { value: "moderate", label: "Moderate issues", icon: "🟠" },
                  { value: "major", label: "Major issues", icon: "🔴" }
                ].map(option => (
                  <label key={option.value} className={`radio-option ${hadProblems === option.value ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="hadProblems"
                      value={option.value}
                      checked={hadProblems === option.value}
                      onChange={(e) => setHadProblems(e.target.value)}
                    />
                    <span className="radio-icon">{option.icon}</span>
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {hadProblems !== "none" && (
                <textarea
                  placeholder="Please describe the problems you encountered..."
                  value={problemsDescription}
                  onChange={(e) => setProblemsDescription(e.target.value)}
                  rows={2}
                  maxLength={1000}
                />
              )}
            </div>

            {/* Understanding */}
            <div className="survey-question">
              <label>Was everything clear and understandable?</label>
              <div className="radio-group">
                {[
                  { value: "clear", label: "Clear", icon: "✅" },
                  { value: "mostly_clear", label: "Mostly clear", icon: "🟢" },
                  { value: "somewhat_confusing", label: "Somewhat confusing", icon: "🟡" },
                  { value: "very_confusing", label: "Very confusing", icon: "🔴" }
                ].map(option => (
                  <label key={option.value} className={`radio-option ${understandingLevel === option.value ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="understandingLevel"
                      value={option.value}
                      checked={understandingLevel === option.value}
                      onChange={(e) => setUnderstandingLevel(e.target.value)}
                    />
                    <span className="radio-icon">{option.icon}</span>
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {(understandingLevel === "somewhat_confusing" || understandingLevel === "very_confusing") && (
                <textarea
                  placeholder="What was confusing or unclear?"
                  value={confusionDescription}
                  onChange={(e) => setConfusionDescription(e.target.value)}
                  rows={2}
                  maxLength={1000}
                />
              )}
            </div>

            <div className="survey-actions">
              <button className="btn-skip" onClick={onClose}>Skip Survey</button>
              <button className="btn-next" onClick={() => setCurrentStep(2)}>Next</button>
            </div>
          </div>
        )}

        {/* Step 2: Improvements & Rating */}
        {currentStep === 2 && (
          <div className="survey-step">
            {/* Improvements */}
            <div className="survey-question">
              <label>Is there anything that could be done better?</label>
              <div className="radio-group">
                {[
                  { value: "no", label: "No improvements needed", icon: "✅" },
                  { value: "minor", label: "Minor improvements", icon: "🟢" },
                  { value: "moderate", label: "Moderate improvements", icon: "🟡" },
                  { value: "major", label: "Major improvements needed", icon: "🔴" }
                ].map(option => (
                  <label key={option.value} className={`radio-option ${needsImprovement === option.value ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="needsImprovement"
                      value={option.value}
                      checked={needsImprovement === option.value}
                      onChange={(e) => setNeedsImprovement(e.target.value)}
                    />
                    <span className="radio-icon">{option.icon}</span>
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {needsImprovement !== "no" && (
                <textarea
                  placeholder="What improvements would you suggest?"
                  value={improvementSuggestions}
                  onChange={(e) => setImprovementSuggestions(e.target.value)}
                  rows={2}
                  maxLength={1000}
                />
              )}
            </div>

            {/* Overall Rating */}
            <div className="survey-question">
              <label>Overall, how would you rate this page?</label>
              <div className="star-rating">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    className={`star ${overallRating >= star ? 'filled' : ''}`}
                    onClick={() => setOverallRating(star)}
                  >
                    ★
                  </button>
                ))}
                <span className="rating-label">
                  {overallRating === 0 && "Click to rate"}
                  {overallRating === 1 && "Poor"}
                  {overallRating === 2 && "Fair"}
                  {overallRating === 3 && "Good"}
                  {overallRating === 4 && "Very Good"}
                  {overallRating === 5 && "Excellent"}
                </span>
              </div>
            </div>

            {/* Would Recommend */}
            <div className="survey-question">
              <label>Would you recommend Expanded Chess to others?</label>
              <div className="radio-group horizontal">
                {[
                  { value: "yes", label: "Yes", icon: "👍" },
                  { value: "maybe", label: "Maybe", icon: "🤷" },
                  { value: "no", label: "No", icon: "👎" }
                ].map(option => (
                  <label key={option.value} className={`radio-option ${wouldRecommend === option.value ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="wouldRecommend"
                      value={option.value}
                      checked={wouldRecommend === option.value}
                      onChange={(e) => setWouldRecommend(e.target.value)}
                    />
                    <span className="radio-icon">{option.icon}</span>
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="survey-actions">
              <button className="btn-back" onClick={() => setCurrentStep(1)}>Back</button>
              <button className="btn-next" onClick={() => setCurrentStep(3)}>Next</button>
            </div>
          </div>
        )}

        {/* Step 3: Comments & Submit */}
        {currentStep === 3 && (
          <div className="survey-step">
            {/* General Comments */}
            <div className="survey-question">
              <label>Any general comments about this page?</label>
              <textarea
                placeholder="Share your thoughts, observations, or feedback..."
                value={generalComments}
                onChange={(e) => setGeneralComments(e.target.value)}
                rows={3}
                maxLength={2000}
              />
            </div>

            {/* Recommendations */}
            <div className="survey-question">
              <label>What suggestions or recommendations do you have?</label>
              <textarea
                placeholder="Any features you'd like to see? Ways to improve the experience?"
                value={recommendations}
                onChange={(e) => setRecommendations(e.target.value)}
                rows={3}
                maxLength={2000}
              />
            </div>

            {/* Reporter info */}
            <div className="survey-question reporter-info">
              <label>Your information (optional)</label>
              <div className="input-row">
                <input
                  type="text"
                  placeholder="Name"
                  value={reporterName}
                  onChange={(e) => setReporterName(e.target.value)}
                  maxLength={100}
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={reporterEmail}
                  onChange={(e) => setReporterEmail(e.target.value)}
                  maxLength={255}
                />
              </div>
            </div>

            <div className="survey-actions">
              <button className="btn-back" onClick={() => setCurrentStep(2)}>Back</button>
              <button
                className="btn-submit"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Survey"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyModal;
